import React from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckOutlined } from '@ant-design/icons';

import {
  HistoryResponseModel,
  AssignmentHistoryCascadeFinancialInfoUpdateStatus,
  HistoryTypeEnum,
} from 'api/adminApiClient';
import {
  getNormalizedCascadeAssignments,
  NormalizedCascadeAssignment,
} from '../HistoryLogCreatedInfo/cascadeHistoryUtils';

import styles from './CustomerCascadeFeeUpdateDetailsInfo.module.less';

type Props = {
  historyItem: HistoryResponseModel;
};

function StatusCell({ assignment }: { assignment: NormalizedCascadeAssignment }) {
  const classMap: Record<string, string> = {
    [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Updated]: styles.statusUpdated,
    [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Failed]: styles.statusFailed,
    [AssignmentHistoryCascadeFinancialInfoUpdateStatus.Skipped]: styles.statusSkipped,
  };
  const className = assignment.statusEnum !== undefined
    ? classMap[assignment.statusEnum] ?? styles.statusSkipped
    : styles.statusSkipped;

  return <span className={className}>{assignment.statusLabel}</span>;
}

const columns: ColumnsType<NormalizedCascadeAssignment> = [
  {
    title: 'Assignment ID',
    dataIndex: 'assignmentKey',
    key: 'assignmentKey',
    width: 160,
  },
  {
    title: 'Scheduler',
    dataIndex: 'scheduler',
    key: 'scheduler',
    width: 90,
    align: 'center',
    render: (value: boolean) => value ? <CheckOutlined className={styles.schedulerIcon} /> : '—',
  },
  {
    title: 'Status',
    key: 'status',
    width: 100,
    render: (_, record) => <StatusCell assignment={record} />,
  },
  {
    title: 'Old Value',
    dataIndex: 'oldValueSummary',
    key: 'oldValue',
    render: (text: string) => text || '—',
  },
  {
    title: 'New Value',
    dataIndex: 'newValueSummary',
    key: 'newValue',
    render: (text: string) => text || '—',
  },
  {
    title: 'Details',
    dataIndex: 'detailsMessage',
    key: 'details',
    render: (text: string | undefined) => text || '—',
  },
];

export default function CustomerCascadeFeeUpdateDetailsInfo(props: Props) {
  const { historyItem } = props;
  const assignments = getNormalizedCascadeAssignments({
    type: HistoryTypeEnum.CustomerAssignmentFeeCascadeUpdateTotalDetails,
    details: historyItem.details,
  });

  if (assignments.length === 0) {
    return (
      <div className={styles.emptyMessage} data-id="cascade-assignment-empty">
        No assignments
      </div>
    );
  }

  return (
    <div className={styles.wrapper} data-id="cascade-assignments-table">
      <Table<NormalizedCascadeAssignment>
        columns={columns}
        dataSource={assignments}
        rowKey={(_, index) => String(index)}
        pagination={false}
        size="small"
        bordered
      />
    </div>
  );
}
